// The rules of an applet, shared by the server (server/lib/applets.js: what may be published and served) and the page
// (client/lib/applets.js: what may be installed and run), like model.js: an applet's manifest.json and what it may
// say, the names in it, the permissions it may ask for, the mounting targets the page has, and how files and versions
// are read. Free of DOM and Node APIs, so both sides import it.

// The version of the page's applet API (@webtop/api and @webtop/components, docs/applets.md). A manifest may say
// which it was written for (`api`); a later major version would refuse it.
export const APPLET_API_VERSION = 1;
export const MANIFEST_VERSION = 1;

// An applet's id: reverse-domain style (com.example.my-applet), lower case; no `_`, which joins an id and a version
// in a key (appletKey), and no `/`.
export const ID_RE = /^[a-z0-9][a-z0-9.-]{1,99}$/;
// A version: major.minor.patch, optionally -pre.release (compareVersions orders them).
export const VERSION_RE = /^\d{1,9}\.\d{1,9}\.\d{1,9}(?:-[0-9a-z][0-9a-z.-]{0,39})?$/i;
// The name of something inside an applet (a widget, a view, a launcher, a target it registers, a service).
export const NAME_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;
// What a `command` tile runs (model.js): an applet's id, or its id and one of its commands, `<id>/<command>`, which
// may carry arguments as a query string, `<id>/<command>?text=Hi` (URL-encoded, so no spaces, `/` or `#`).
export const COMMAND_TARGET_RE = /^[a-z0-9][a-z0-9.-]{1,99}(?:\/[a-z0-9][a-z0-9-]{0,39}(?:\?[^\s/#]{0,500})?)?$/;
// A file in the package: a relative path of plain segments, no `..`, no leading `/`, no backslash, no control chars.
export const PATH_RE = /^(?!\/)(?:[A-Za-z0-9._@-][A-Za-z0-9._@ -]{0,99}\/)*[A-Za-z0-9._@-][A-Za-z0-9._@ -]{0,99}$/;
export const isPackagePath = (p) => typeof p === 'string' && p.length <= 400 && PATH_RE.test(p) && !p.split('/').some((seg) => seg === '..' || seg === '.');

export const TYPES = ['application', 'service', 'extension'];
// Where an applet is meant to run: 'prod' (the default: every browser) or 'dev' (a demo or a developer's tool, listed and
// loaded only in a browser with Developer Mode on, Settings > Applets; client/lib/applets.js).
export const ENVIRONMENTS = ['prod', 'dev'];
export const ACTIVATION_EVENTS = ['onStartup', 'onLaunch', 'onWidget', 'onCommand', 'onEvent', 'onService'];
// What an applet may ask for in `permissions`, each gating a namespace of @webtop/api (docs/applets.md). The text is
// what the Applets overlay shows for it.
export const PERMISSIONS = {
  storage: 'Keep its own data in this browser, and on the webtops it is used on',
  notifications: 'Show notifications and dialogs',
  views: 'Open panels: windows and sidebars',
  windows: 'Open floating windows',
  targets: 'Add places other applets can open panels in',
  widgets: 'Provide widgets for webtops',
  launchers: 'Add entries to the launcher',
  events: 'Follow what happens on the page (the webtop opened, redraws)',
  webtop: 'Read the open webtop (its title, address and tiles), go to another, and show a newer copy of it',
  services: 'Offer services to other applets, and use theirs',
  channel: 'Hear what its code on the server sends this page while it works',
  page: 'Do what you can do on the page: open its views, point out tiles, export, lock, sign in or out, open tabs',
  backend: 'Call its own code on the server',
  audio: 'Listen through your microphone once this site may use it, play sound, and change the volume and devices',
  applets: 'Install, update, turn on and off and remove applets in this browser, and publish them',
};
// The mounting targets the page itself provides (client/lib/views.js); applets with `targets` may register more.
// `overlay` is the full-screen view the launcher and the page's other overlays are (client/lib/overlay.js): one at a time.
// `desktop` is the webtop's own place: a View shown there stands in for the open webtop, one at a time.
export const TARGETS = ['window', 'sidebar:left', 'sidebar:right', 'overlay', 'desktop'];
export const DEFAULT_TARGET = 'window';

export const MANIFEST_FILE = 'manifest.json';
export const MANIFEST_MAX = 64 * 1024; // bytes of manifest.json
export const PACKAGE_MAX = 20 * 1024 * 1024; // bytes of a package (a ZIP, or the files of a folder together)
export const FILES_MAX = 2000; // files in a package
export const DESCRIPTION_MAX = 500;
export const SIZES_ALL = ['2x2', '1x1', '2x1', '3x1', '3x2', '3x3', '3x4', '4x2', '4x3', '4x4', '6x3']; // what a widget may offer

const fail = (message) => { throw new Error(message); };
const str = (v, max, what, { required = false, re = null } = {}) => {
  if (v === undefined || v === null || v === '') {
    if (required) fail(`manifest: "${what}" is required`);
    return undefined;
  }
  if (typeof v !== 'string') fail(`manifest: "${what}" must be a string`);
  const s = v.trim().slice(0, max);
  if (re && !re.test(s)) fail(`manifest: "${what}" is not valid (${s.slice(0, 60)})`);
  return s;
};
const filePath = (v, what, required = false) => {
  const s = str(v, 400, what, { required });
  if (s !== undefined && !isPackagePath(s)) fail(`manifest: "${what}" must be a relative path inside the package`);
  return s;
};
const list = (v, what) => {
  if (v === undefined) return [];
  if (!Array.isArray(v)) fail(`manifest: "${what}" must be a list`);
  return v;
};
const unique = (items, what) => {
  const seen = new Set();
  for (const it of items) { if (seen.has(it.id)) fail(`manifest: "${what}" lists "${it.id}" twice`); seen.add(it.id); }
  return items;
};

/**
 * manifest.json as the page and the server accept it: the fields below, cleaned and capped, or an Error saying what
 * is wrong (the message is for the developer). Unknown fields are dropped. `raw` is the parsed JSON.
 */
export function checkManifest(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) fail('manifest: must be a JSON object');
  if (raw.manifest_version !== MANIFEST_VERSION) fail(`manifest: "manifest_version" must be ${MANIFEST_VERSION}`);
  const m = {
    manifest_version: MANIFEST_VERSION,
    id: str(raw.id, 100, 'id', { required: true, re: ID_RE }),
    name: str(raw.name, 80, 'name', { required: true }),
    version: str(raw.version, 60, 'version', { required: true, re: VERSION_RE }),
    description: str(raw.description, DESCRIPTION_MAX, 'description') ?? '',
    type: raw.type === undefined ? 'application' : str(raw.type, 20, 'type'),
    environment: raw.environment === undefined ? 'prod' : str(raw.environment, 10, 'environment'),
    main: filePath(raw.main, 'main'),
    backend: filePath(raw.backend, 'backend'),
    icon: filePath(raw.icon, 'icon'),
    api: raw.api === undefined ? APPLET_API_VERSION : raw.api,
    // Part of webtop itself (the launcher): loaded in every browser whatever APPLET_THIRD_PARTY and APPLET_BACKENDS say,
    // installed and kept at the server's version by the page, never uninstalled or disabled. Only an applet built into
    // the server (applets/ in its checkout) is taken as required; a package that says so is refused for publishing.
    required: raw.required === undefined ? false : raw.required,
  };
  if (typeof m.required !== 'boolean') fail('manifest: "required" must be true or false');
  // The names of .env settings its backend gets (the storage applet's database and bucket): only a required applet's
  // backend is given them (server/lib/backends.js); every other backend runs with none of .env.
  m.env = [...new Set(list(raw.env, 'env').map((n) => str(n, 64, 'env', { required: true, re: /^[A-Z][A-Z0-9_]{0,63}$/ })))].slice(0, 20);
  if (!TYPES.includes(m.type)) fail(`manifest: "type" must be one of ${TYPES.join(', ')}`);
  if (!ENVIRONMENTS.includes(m.environment)) fail(`manifest: "environment" must be one of ${ENVIRONMENTS.join(', ')}`);
  if (!m.main && !m.backend) fail('manifest: "main" (the page entry) or "backend" (the server entry) is required');
  if (!Number.isInteger(m.api) || m.api < 1 || m.api > APPLET_API_VERSION) fail(`manifest: "api" must be ${APPLET_API_VERSION} (this webtop's applet API)`);
  if (raw.author !== undefined) {
    const a = typeof raw.author === 'string' ? { name: raw.author } : raw.author;
    if (!a || typeof a !== 'object') fail('manifest: "author" must be a name or {name, url}');
    m.author = { name: str(a.name, 80, 'author.name') ?? '', url: str(a.url, 500, 'author.url') ?? '' };
    if (m.author.url && !/^https?:\/\/\S+$/i.test(m.author.url)) fail('manifest: "author.url" must be an http(s) address');
  }
  m.activationEvents = [...new Set(list(raw.activationEvents, 'activationEvents').map((e) => str(e, 40, 'activationEvents')))];
  for (const e of m.activationEvents) if (!ACTIVATION_EVENTS.includes(e)) fail(`manifest: unknown activation event "${e}"`);
  m.permissions = [...new Set(list(raw.permissions, 'permissions').map((p) => str(p, 40, 'permissions')))];
  for (const p of m.permissions) if (!PERMISSIONS[p]) fail(`manifest: unknown permission "${p}"`);
  m.styles = list(raw.styles, 'styles').map((s) => filePath(s, 'styles', true)).slice(0, 20);
  const c = raw.contributes ?? {};
  if (!c || typeof c !== 'object' || Array.isArray(c)) fail('manifest: "contributes" must be an object');
  m.contributes = {
    widgets: unique(list(c.widgets, 'contributes.widgets').slice(0, 50).map((w) => ({
      id: str(w?.id, 40, 'contributes.widgets[].id', { required: true, re: NAME_RE }),
      name: str(w?.name, 80, 'contributes.widgets[].name', { required: true }),
      about: str(w?.about, 200, 'contributes.widgets[].about') ?? '',
      sizes: sizes(w?.sizes),
    })), 'contributes.widgets'),
    views: unique(list(c.views, 'contributes.views').slice(0, 50).map((v) => ({
      id: str(v?.id, 40, 'contributes.views[].id', { required: true, re: NAME_RE }),
      name: str(v?.name, 80, 'contributes.views[].name', { required: true }),
      defaultTarget: str(v?.defaultTarget, 60, 'contributes.views[].defaultTarget') ?? DEFAULT_TARGET,
    })), 'contributes.views'),
    launchers: unique(list(c.launchers, 'contributes.launchers').slice(0, 50).map((l) => ({
      id: str(l?.id, 40, 'contributes.launchers[].id', { required: true, re: NAME_RE }),
      name: str(l?.name, 80, 'contributes.launchers[].name', { required: true }),
    })), 'contributes.launchers'),
    // What a shortcut may run (a `command` tile, `@<applet>/<command>` in its Opens field): declared here so a tile
    // works before the applet is active and the field can suggest it; the code connects each with commands.register.
    commands: unique(list(c.commands, 'contributes.commands').slice(0, 50).map((k) => ({
      id: str(k?.id, 40, 'contributes.commands[].id', { required: true, re: NAME_RE }),
      name: str(k?.name, 80, 'contributes.commands[].name', { required: true }),
      about: str(k?.about, 200, 'contributes.commands[].about') ?? '',
      // the arguments it takes (`?text=Hi`), for the Opens field to suggest; the function gets them as {text: 'Hi'}
      params: unique(list(k?.params, 'contributes.commands[].params').slice(0, 10).map((p) => ({
        id: str(p?.id, 40, 'contributes.commands[].params[].id', { required: true, re: NAME_RE }),
        about: str(p?.about, 200, 'contributes.commands[].params[].about') ?? '',
      })), 'contributes.commands[].params'),
    })), 'contributes.commands'),
    targets: unique(list(c.targets, 'contributes.targets').slice(0, 20).map((t) => ({
      id: str(t?.id, 60, 'contributes.targets[].id', { required: true, re: /^[a-z0-9][a-z0-9-]{0,39}:[a-z0-9][a-z0-9-]{0,39}$/ }),
      name: str(t?.name, 80, 'contributes.targets[].name', { required: true }),
    })), 'contributes.targets'),
  };
  if (m.contributes.widgets.length && !m.permissions.includes('widgets')) fail('manifest: contributing widgets needs the "widgets" permission');
  if (m.contributes.targets.length && !m.permissions.includes('targets')) fail('manifest: contributing targets needs the "targets" permission');
  if (m.backend && !m.permissions.includes('backend') && m.main) fail('manifest: a backend the page calls needs the "backend" permission');
  if (raw.webtop !== undefined) {
    if (!raw.webtop || typeof raw.webtop !== 'object') fail('manifest: "webtop" must be an object');
    m.webtop = { minVersion: str(raw.webtop.minVersion, 60, 'webtop.minVersion', { re: VERSION_RE }) ?? null };
  }
  return m;
}
// A widget's sizes: a non-empty list of grid sizes the page knows (SIZES_ALL), the first the default; 2x2 when not given.
function sizes(v) {
  if (v === undefined) return ['2x2'];
  if (!Array.isArray(v) || !v.length) fail('manifest: a widget\'s "sizes" must be a non-empty list');
  const out = [...new Set(v.map((s) => String(s)))];
  for (const s of out) if (!SIZES_ALL.includes(s)) fail(`manifest: unknown widget size "${s}" (one of ${SIZES_ALL.join(', ')})`);
  return out;
}

/** The key an applet version is stored under: `<id>_<version>` (split at the first `_`, which an id never has). */
export const appletKey = (id, version) => `${id}_${version}`;
export function splitKey(key) {
  const i = key.indexOf('_');
  return i > 0 ? { id: key.slice(0, i), version: key.slice(i + 1) } : null;
}

/** Orders versions: negative when a < b. Numbers first, then a pre-release sorts before the release it precedes. */
export function compareVersions(a, b) {
  const pa = String(a).split('-'), pb = String(b).split('-');
  const na = pa[0].split('.').map(Number), nb = pb[0].split('.').map(Number);
  for (let i = 0; i < 3; i++) if (na[i] !== nb[i]) return (na[i] ?? 0) - (nb[i] ?? 0);
  if (!pa[1] && !pb[1]) return 0;
  if (!pa[1]) return 1;
  if (!pb[1]) return -1;
  return pa[1] < pb[1] ? -1 : pa[1] > pb[1] ? 1 : 0;
}
export const newerVersion = (a, b) => compareVersions(a, b) > 0;

// What a file of a package is served or loaded as, by its extension.
const FILE_TYPES = {
  js: 'text/javascript', mjs: 'text/javascript', css: 'text/css', json: 'application/json', html: 'text/html',
  txt: 'text/plain', md: 'text/markdown', map: 'application/json', svg: 'image/svg+xml', png: 'image/png',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', avif: 'image/avif', ico: 'image/x-icon',
  woff: 'font/woff', woff2: 'font/woff2', ttf: 'font/ttf', wasm: 'application/wasm', mp3: 'audio/mpeg', ogg: 'audio/ogg',
  wav: 'audio/wav', mp4: 'video/mp4', webm: 'video/webm',
};
export const fileType = (path) => FILE_TYPES[String(path).split('.').pop().toLowerCase()] ?? 'application/octet-stream';
export const isScript = (path) => /\.m?js$/i.test(path);

/** What a list of applets shows of one (the Applets overlay, GET /api/applets): the manifest's headline fields. */
export const appletSummary = (m) => ({
  id: m.id, name: m.name, version: m.version, description: m.description, type: m.type, environment: m.environment ?? 'prod', author: m.author ?? null,
  icon: m.icon ?? null, permissions: m.permissions, main: !!m.main, backend: !!m.backend, required: !!m.required,
  widgets: m.contributes.widgets.map((w) => ({ id: w.id, name: w.name })),
  commands: (m.contributes.commands ?? []).map((k) => ({ id: k.id, name: k.name, params: (k.params ?? []).map((p) => p.id) })),
});
